import { useEffect, useState } from 'react';
import './Banner.css';
import './BannerVolvia.css';
import './BannerRenault.css';
import './BannerKia.css';
import { Action, AdvisoryContent } from '../../../common/global-types/AdvisoryContent';
import Modal from '../modal/Modal';
import useViewPairing from '../modal/useViewPairing';
import APIUtil from '../../../common/api/APIUtil';
import useTracking from '../../../common/tracking/useTracking';
import { getIconSizing } from '../../../common/components/action/IconSize';

type BannerProps = {
  content: AdvisoryContent;
  setContent: (content: AdvisoryContent[] | undefined) => void;
  viewId: number | undefined;
  setViewId: (viewId: number | undefined) => void;
  proxyPath: string | null;
};

const defaultTheme = 'lightBeige';

function Banner(
  { 
    content,
    setContent,
    viewId,
    setViewId,
    proxyPath
  }: BannerProps
) {
  const [viewTheme, setViewTheme] = useState<string | undefined>(
    content.contentViews?.find((cv) => cv.contentViewId === viewId)?.viewTheme
  );

  const [loading, setLoading] = useState<boolean>(false);

  const { primaryViewId, modalViewId } = useViewPairing(
    viewId,
    content.contentViews!,
    content.initialView
  );
  const trackVisibilityElement = useTracking(content, primaryViewId);

  useEffect(() => {
    setViewTheme(content.contentViews?.find((cv) => cv.contentViewId === primaryViewId)?.viewTheme);
  }, [content.contentViews, primaryViewId]);

  async function handleOnClick(action: Action) {
    if (action.callbackPath) APIUtil.handleCallbackUrl(proxyPath, action.callbackPath);

    if (action.nextContentPath) {
      setLoading(true);

      const result = await APIUtil.handleContentUrl(action.nextContentPath);

      setLoading(false);

      if (!result) return;

      setContent([result]);
    }

    if (action.loadView) setViewId(action.loadView!);
  }

  function isEmptyOrWhitespace(str: string | null | undefined): boolean {
    return !str || str.trim().length === 0;
  }

  const currentView = content.contentViews?.find((cv) => cv.contentViewId === primaryViewId)?.viewElements;

  if (!currentView) return null;

  return (
    <>
      <div
        className={viewTheme === defaultTheme
          ? 'light-beige if card elevated advisory-banner-container'
          : `${viewTheme} advisory-banner-container`}
      >
        <div className="text-container">
          <div>
            {currentView.icons && currentView.icons[0] && (
              <ids-icon {...{ [currentView.icons[0].name]: true, [`${currentView.icons[0].group}-32`]: true }} />            
            )}
            {!isEmptyOrWhitespace(currentView.paragraphs[3]) && (
              <div className="if tag green">
                <span className="if typography paragraph medium">{currentView.paragraphs[3]}</span>
              </div>
            )}
            <h3
              className={viewTheme === defaultTheme ? 'if typography heading small' : ''}
              ref={trackVisibilityElement}
            >
              {currentView.headlines[0]}
            </h3>
            {!isEmptyOrWhitespace(currentView.paragraphs[0]) && (
              <p className={viewTheme === defaultTheme ? 'if typography paragraph medium' : ''}>
                {currentView.paragraphs[0]}
              </p>
            )}
          </div>
          {(!isEmptyOrWhitespace(currentView.paragraphs[1]) || !isEmptyOrWhitespace(currentView.paragraphs[2])) && (
            <div>
              {!isEmptyOrWhitespace(currentView.paragraphs[1])  && (
                <p className={viewTheme === defaultTheme ? 'if typography paragraph medium spacing-small' : ''}>
                  {currentView.paragraphs[1]}
                </p>
              )}
              {!isEmptyOrWhitespace(currentView.paragraphs[2]) && (
                <span className={viewTheme === defaultTheme ? 'if typography paragraph large bold' : ''}>
                  {currentView.paragraphs[2]}
                </span>
              )}
              {currentView.actions && currentView.actions.length > 2 && (
                <a
                  className={currentView.actions[2].idsClassName || undefined}
                  href={currentView.actions[2].url!}
                  target={currentView.actions[2].target ?? '_self'}
                >
                  {currentView.actions[2].text}
                  {currentView.actions[2].idsIconName && currentView.actions[2].idsIconGroup && (
                      <ids-icon {...{ [currentView.actions[2].idsIconName]: true, [`${currentView.actions[2].idsIconGroup}-${getIconSizing(currentView.actions[2].idsClassName)}`]: true }} />
                    )}
                </a>
              )}
            </div>
          )}
          {currentView.actions && currentView.actions.length > 0 && (            
            <div className="actions">
              {currentView.actions.map((action, index) => {
                if (index >= 2) return null;

                return action.url ? (
                  <a
                    key={action.text}
                    className={isEmptyOrWhitespace(action.idsClassName) ? "if link standalone" : action.idsClassName} 
                    href={action.url}
                    target={action.target ?? '_self'}
                    onClick={() => handleOnClick(action)}
                  >
                    {action.text}
                    {action.idsIconName && action.idsIconGroup && (
                      <ids-icon {...{ [action.idsIconName]: true, [`${action.idsIconGroup}-${getIconSizing(action.idsClassName)}`]: true }} />
                    )}
                  </a>
                ) : (
                  <button
                    key={action.text}
                    type="button"
                    disabled={!!action.nextContentPath && loading}
                    className={action.idsClassName || undefined}
                    onClick={() => handleOnClick(action)}
                  >
                    {action.text}
                    {action.idsIconName && action.idsIconGroup && (
                      <ids-icon {...{ [action.idsIconName]: true, [`${action.idsIconGroup}-${getIconSizing(action.idsClassName)}`]: true }} />
                    )}
                    {action.nextContentPath && loading && <span className="if loader dots x-small" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {currentView.media && (
          <div className="image-container">
            <img src={currentView.media[0].url} alt={currentView.media[0].alt} />
            {!isEmptyOrWhitespace(currentView.paragraphs[3]) && (
              <div className="if tag green">
                <span className="if typography paragraph medium">{currentView.paragraphs[3]}</span>
              </div>
            )}
          </div>
        )}
      </div>
      <Modal
        content={content}
        viewId={modalViewId}
        setViewId={setViewId}
        proxyPath={proxyPath}
      />
    </>
  );
}

export default Banner;
